import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd, Clear } from '@mui/icons-material';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

import { useNavigate } from 'react-router-dom';

const StaffInitialization = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Get hotel code from localStorage
  const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
  const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';

  // Form state
  const [formData, setFormData] = useState({
    hotel_code: hotelCode,
    staff_id: '',
    first_name: '',
    other_names: '',
    department: '',
    phone: '',
    email: '',
    gender: '',
    password: '',
    confirm_password: '',
    user_type: 'staff',
  });

  // Available departments
  const departments = [
    'General Manager',
    'Reception',
    'Bar/Restaurant',
    'Account',
    'Store keeping',
    'Cashier',
    'Housekeeping',
    'Security',
    'Maintenance',
    'Spa',
    'Events',
    'Sales & Marketing',
    'HR',
    'ICT',
  ];

  // User types
  const userTypes = [
    { value: 'General_Manager', label: 'General Manager' },
    { value: 'receptionist', label: 'Receptionist' },
    { value: 'Restaurant/Bar', label: 'Restaurant/Bar' },
    { value: 'Account', label: 'Account' },
    { value: 'Storekeeping', label: 'Store keeping' },
    { value: 'Cashier', label: 'Cashier' },
    { value: 'Requisition', label: 'Requisition' },
    { value: 'hod', label: 'HOD' },
    { value: 'hr', label: 'HR Staff' },
   
  ];

  const genders = ['Male', 'Female'];

  // Validation errors
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Generate random staff ID
  const generateStaffId = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `STAFF${timestamp}${randomStr}`;
  };

  // Validation functions
  const validateField = (field, value) => {
    switch (field) {
      case 'first_name':
        if (!value.trim()) return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        if (!/^[A-Za-z\s'-]+$/.test(value.trim())) return 'First name can only contain letters, spaces, apostrophes, and hyphens';
        return '';

      case 'other_names':
        if (value.trim() && !/^[A-Za-z\s'-]+$/.test(value.trim())) {
          return 'Other names can only contain letters, spaces, apostrophes, and hyphens';
        }
        return '';

      case 'department':
        if (!value) return 'Department is required';
        return '';

      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        const cleanPhone = value.replace(/\D/g, '');
        if (cleanPhone.length < 10) return 'Phone number must be at least 10 digits';
        if (cleanPhone.length > 15) return 'Phone number must not exceed 15 digits';
        if (!/^[0-9]+$/.test(cleanPhone)) return 'Phone number can only contain digits';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
        if (!emailRegex.test(value.trim())) return 'Please enter a valid email address (e.g., name@example.com)';
        if (value.trim().length > 100) return 'Email address is too long';
        return '';

      case 'gender':
        if (!value) return 'Gender is required';
        return '';

      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        if (value.length > 50) return 'Password must not exceed 50 characters';
        if (!/(?=.*[A-Za-z])/.test(value)) return 'Password must contain at least one letter';
        if (!/(?=.*\d)/.test(value)) return 'Password must contain at least one number';
        return '';

      case 'confirm_password':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';

      case 'staff_id':
        if (value && !/^[A-Za-z0-9_-]+$/.test(value)) {
          return 'Staff ID can only contain letters, numbers, underscores, and hyphens';
        }
        return '';

      default:
        return '';
    }
  };

  const validateAllFields = () => {
    const fieldsToValidate = ['first_name', 'department', 'phone', 'email', 'gender', 'password', 'confirm_password'];
    const newErrors = {};
    let isValid = true;

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Validate staff_id if provided
    if (formData.staff_id) {
      const staffIdError = validateField('staff_id', formData.staff_id);
      if (staffIdError) {
        newErrors.staff_id = staffIdError;
        isValid = false;
      }
    }

    // Validate other_names if provided
    if (formData.other_names) {
      const otherNamesError = validateField('other_names', formData.other_names);
      if (otherNamesError) {
        newErrors.other_names = otherNamesError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleFieldBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // Special handling for phone - allow only digits
    if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 15);
    }
    
    setFormData({
      ...formData,
      [field]: value,
    });
    
    // Clear error for this field if it was touched
    if (touchedFields[field] && errors[field]) {
      const error = validateField(field, value);
      if (!error) {
        setErrors({
          ...errors,
          [field]: '',
        });
      }
    }
    
    // Clear success message when user starts typing
    if (success) setSuccess(false);
    if (error) setError('');
  };

  // Auto-generate staff ID on focus if empty
  const handleStaffIdFocus = () => {
    if (!formData.staff_id) {
      setFormData({
        ...formData,
        staff_id: generateStaffId(),
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    } else if (!/^[A-Za-z\s'-]+$/.test(formData.first_name.trim())) {
      newErrors.first_name = 'First name can only contain letters, spaces, apostrophes, and hyphens';
    }

    if (formData.other_names.trim() && !/^[A-Za-z\s'-]+$/.test(formData.other_names.trim())) {
      newErrors.other_names = 'Other names can only contain letters, spaces, apostrophes, and hyphens';
    }

    if (!formData.department) {
      newErrors.department = 'Department is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        newErrors.phone = 'Phone number must be at least 10 digits';
      } else if (cleanPhone.length > 15) {
        newErrors.phone = 'Phone number must not exceed 15 digits';
      } else if (!/^[0-9]+$/.test(cleanPhone)) {
        newErrors.phone = 'Phone number can only contain digits';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      } else if (formData.email.trim().length > 100) {
        newErrors.email = 'Email address is too long';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (formData.password.length > 50) {
      newErrors.password = 'Password must not exceed 50 characters';
    } else if (!/(?=.*[A-Za-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    if (formData.staff_id && !/^[A-Za-z0-9_-]+$/.test(formData.staff_id)) {
      newErrors.staff_id = 'Staff ID can only contain letters, numbers, underscores, and hyphens';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      hotel_code: hotelCode,
      staff_id: '',
      first_name: '',
      other_names: '',
      department: '',
      phone: '',
      email: '',
      gender: '',
      password: '',
      confirm_password: '',
      user_type: 'staff',
    });
    setErrors({});
    setTouchedFields({});
    setSuccess(false);
    setError('');
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      // Mark all fields as touched to show errors
      const allFields = ['first_name', 'department', 'phone', 'email', 'gender', 'password', 'confirm_password'];
      const newTouched = {};
      allFields.forEach(field => { newTouched[field] = true; });
      if (formData.staff_id) newTouched.staff_id = true;
      if (formData.other_names) newTouched.other_names = true;
      setTouchedFields(newTouched);
      return;
    }

    setLoading(true);
    setError('');

    // Prepare payload
    const payload = {
      hotel_code: formData.hotel_code,
      staff_id: formData.staff_id || generateStaffId(),
      first_name: formData.first_name.trim(),
      other_names: formData.other_names.trim(),
      department: formData.department,
      phone: formData.phone.trim(),
      email: formData.email.trim().toLowerCase(),
      gender: formData.gender,
      password: formData.password,
      user_type: formData.user_type,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hotel/staff/staff_register.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(true);
        // Optional: Store in localStorage if needed
        localStorage.setItem('initializedStaff', JSON.stringify(payload));
        
        // Auto reset after 3 seconds
        setTimeout(() => {
          handleReset();
          setSuccess(false);
        }, 3000);
      } else {
        const errorMsg = result.message || result.error || 'Registration failed';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get error for a field
  const getFieldError = (field) => {
    return touchedFields[field] && errors[field] ? errors[field] : '';
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.clear();
            navigate('/Entri');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

      
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 4,
                width: '100%',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                Staff Initialization
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Register new staff members with their personal information, department, and access credentials.
              </Typography>

              {/* Success Message */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
                  Staff member registered successfully! The form has been reset.
                </Alert>
              )}

              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Staff ID */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Staff ID"
                    placeholder="Auto-generated if left empty"
                    value={formData.staff_id}
                    onChange={handleInputChange('staff_id')}
                    onFocus={handleStaffIdFocus}
                    onBlur={() => handleFieldBlur('staff_id')}
                    error={!!getFieldError('staff_id')}
                    helperText={getFieldError('staff_id') || 'Leave empty for auto-generation (letters, numbers, _, - only)'}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonAdd color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Hotel Code */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hotel Code"
                    value={formData.hotel_code}
                    disabled
                    helperText="Automatically assigned from your profile"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Clear color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* First Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    value={formData.first_name}
                    onChange={handleInputChange('first_name')}
                    onBlur={() => handleFieldBlur('first_name')}
                    error={!!getFieldError('first_name')}
                    helperText={getFieldError('first_name') || 'Enter first name (letters only)'}
                    disabled={loading}
                  />
                </Grid>

                {/* Other Names */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Other Names"
                    value={formData.other_names}
                    onChange={handleInputChange('other_names')}
                    onBlur={() => handleFieldBlur('other_names')}
                    error={!!getFieldError('other_names')}
                    helperText={getFieldError('other_names') || 'Middle and/or last name (optional, letters only)'}
                    disabled={loading}
                  />
                </Grid>

                {/* Department */}
                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth 
                    required 
                    error={!!getFieldError('department')} 
                    disabled={loading}
                  >
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={formData.department}
                      onChange={handleInputChange('department')}
                      onBlur={() => handleFieldBlur('department')}
                      label="Department"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">Select Department</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                    {getFieldError('department') && (
                      <FormHelperText error>{getFieldError('department')}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* User Type / Role */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={loading}>
                    <InputLabel>User Type / Role</InputLabel>
                    <Select
                      value={formData.user_type}
                      onChange={handleInputChange('user_type')}
                      label="User Type / Role"
                      sx={{ minWidth: 150 }}
                    >
                      {userTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select the staff role and permissions level</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    onBlur={() => handleFieldBlur('phone')}
                    error={!!getFieldError('phone')}
                    helperText={getFieldError('phone') || 'Enter a valid 10-15 digit phone number (numbers only)'}
                    disabled={loading}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    onBlur={() => handleFieldBlur('email')}
                    error={!!getFieldError('email')}
                    helperText={getFieldError('email') || 'Enter a valid email address'}
                    disabled={loading}
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12} md={6}>
                  <FormControl 
                    fullWidth 
                    required 
                    error={!!getFieldError('gender')} 
                    disabled={loading}
                  >
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender}
                      onChange={handleInputChange('gender')}
                      onBlur={() => handleFieldBlur('gender')}
                      label="Gender"
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      {genders.map((gender) => (
                        <MenuItem key={gender} value={gender}>
                          {gender}
                        </MenuItem>
                      ))}
                    </Select>
                    {getFieldError('gender') && (
                      <FormHelperText error>{getFieldError('gender')}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Password */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    onBlur={() => handleFieldBlur('password')}
                    error={!!getFieldError('password')}
                    helperText={getFieldError('password') || 'Minimum 6 characters, at least one letter and one number'}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VisibilityOff color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Confirm Password */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={handleInputChange('confirm_password')}
                    onBlur={() => handleFieldBlur('confirm_password')}
                    error={!!getFieldError('confirm_password')}
                    helperText={getFieldError('confirm_password')}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VisibilityOff color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            aria-label="toggle confirm password visibility"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    px: 4,
                  }}
                >
                  Reset
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    px: 4,
                    backgroundColor: '#C84646',
                    '&:hover': {
                      backgroundColor: '#b33c3c',
                    },
                  }}
                >
                  Register Staff
                </Button>
              </Box>

              {loading && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Registering staff member...
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StaffInitialization;